// src/synchronizers/AudioSyncCoordinator.js
// ✅ VERSÃO COM BED SEPARADA - Sistema Profissional de Rádio
// 
// ARQUITETURA:
// ┌─────────────────────────────────────────────────────────────┐
// │ TRILHA 1: PLAYLIST (música) - ducking FIXO durante locução │
// │ TRILHA 2: BED (cama) - breathing (sobe/desce nas pausas)   │
// │ TRILHA 3: LOCUÇÃO - volume 100% sempre                     │
// └─────────────────────────────────────────────────────────────┘

import { initGlobalAudio } from '../../utils/audioManager';

export class AudioSyncCoordinator {
  constructor() {
    if (AudioSyncCoordinator.instance) {
      return AudioSyncCoordinator.instance;
    }

    // Inicializa áudio global (música/playlist)
    this.audioManager = initGlobalAudio();
    this.audioElement = this.audioManager.audioElement;
    this.blockReasons = new Map();
    this.modules = new Map();

    this.isDucked = false;
    this.originalVolume = 1.0;

    // 🔥 NOVO: Elemento de áudio para BED (trilha de cama)
    this.bedElement = null;
    this.bedVolume = 0.6;  // Volume base da bed
    this.bedActive = false;

    // Controle do breathing (agora na BED, não na playlist)
    this.breathingActive = false;
    this.breathingInterval = null;
    this.currentPauseMap = null;
    this.breathingStartTime = null;
    this.currentTransition = null;

    // Compat com código antigo / useAudio
    this.isBlocked = false;
    this.blockReason = null;
    this.onUnblockCallbacks = new Set();

    // Criar elemento da bed
    this._createBedElement();

    AudioSyncCoordinator.instance = this;
    console.log('[Coordinator] ✅ Singleton criado (com BED separada)');
  }

  /**
   * Cria elemento de áudio para a BED
   * @private
   */
  _createBedElement() {
    if (typeof document === 'undefined') return;

    let el = document.getElementById('bed-audio-element');
    if (!el) {
      el = document.createElement('audio');
      el.id = 'bed-audio-element';
      el.style.display = 'none';
      el.loop = true;  // Beds são em loop
      el.volume = 0;   // Começa mudo
      document.body.appendChild(el);
    }
    this.bedElement = el;
    console.log('[Coordinator] 🎵 Elemento BED criado');
  }

  // --------- registro de módulos ---------

  registerModule(name, module) {
    this.modules.set(name, module);
    console.log(`[Coordinator] ✅ Módulo '${name}' registrado`);
  }

  unregisterModule(name) {
    if (this.modules.has(name)) {
      this.modules.delete(name);
      console.log(`[Coordinator] 🧹 Módulo '${name}' removido`);
    }
  }

  // --------- bloqueio de avanço ---------

  blockAdvance(reason) {
    const key = reason || 'unknown';
    this.blockReasons.set(key, true);
    this.isBlocked = true;
    this.blockReason = key;
    console.log(`[Coordinator] 🔒 Bloqueado: ${key}`);
  }

  addOnUnblockCallback(cb) {
    if (typeof cb === 'function') {
      this.onUnblockCallbacks.add(cb);
    }
  }

  unblockAdvance(reason) {
    const key = reason || this.blockReason;

    if (key) {
      this.blockReasons.delete(key);
    }

    this.isBlocked = this.blockReasons.size > 0;
    this.blockReason = this.isBlocked
      ? Array.from(this.blockReasons.keys()).at(-1)
      : null;

    console.log(
      `[Coordinator] 🔓 Desbloqueado: ${reason || '(sem motivo)'} | ainda bloqueado? ${this.isBlocked}`,
    );

    if (!this.isBlocked && this.onUnblockCallbacks && this.onUnblockCallbacks.size > 0) {
      const callbacks = Array.from(this.onUnblockCallbacks);
      this.onUnblockCallbacks.clear();

      callbacks.forEach((cb) => {
        try {
          cb();
        } catch (err) {
          console.error('[Coordinator] Erro em callback onUnblock:', err);
        }
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // DUCKING DA PLAYLIST (FIXO durante locução)
  // ═══════════════════════════════════════════════════════════════

  /**
   * FASE 1: Aplica ducking inicial na playlist e inicia BED (volume fixo)
   * Fade LENTO para entrada suave do locutor
   * @param {string} bedUrl - URL da trilha de cama (opcional)
   */
  async applyDucking(type = 'spot', level = 0.25, bedUrl = null) {
    const audio = this.audioManager?.audioElement;
    if (!audio) return;

    if (!this.isDucked) {
      this.originalVolume = audio.volume;
    }

    // 🔥 Ducking inicial na PLAYLIST
    const initialLevel = level;
    
    // 🔥 Transição LENTA para entrada suave (1.5 segundos)
    // 30 steps × 50ms = 1500ms de fade
    const steps = 30;
    const stepDelay = 50;
    const start = audio.volume;
    const target = initialLevel * this.originalVolume;
    const delta = target - start;

    console.log(`[Coordinator] 🎛️ Iniciando fade de entrada (${steps * stepDelay}ms)...`);

    for (let i = 0; i < steps; i += 1) {
      const t = (i + 1) / steps;
      audio.volume = Math.max(0, Math.min(1, start + delta * t));
      await new Promise((r) => setTimeout(r, stepDelay));
    }

    audio.volume = target;
    this.isDucked = true;

    console.log(`[Coordinator] 🎛️ Playlist ducking inicial → ${(initialLevel * 100).toFixed(0)}%`);

    // 🔥 Iniciar BED com volume FIXO (só cama, não participa do breathing)
    if (bedUrl && this.bedElement) {
      await this._startBed(bedUrl);
    }
  }

  /**
   * Inicia a trilha de cama (BED)
   * @private
   */
  async _startBed(bedUrl) {
    if (!this.bedElement) return;

    try {
      // 🔥 Converter URL relativa para absoluta (usar backend 3007, não frontend 3000)
      let fullBedUrl = bedUrl;
      if (bedUrl && !/^https?:\/\//i.test(bedUrl)) {
        const apiBase = this.audioElement?.src?.match(/^(https?:\/\/[^/]+)/)?.[1] 
                       || 'http://localhost:3007';
        fullBedUrl = `${apiBase}${bedUrl.startsWith('/') ? '' : '/'}${bedUrl}`;
      }
      
      console.log(`[Coordinator] 📂 Carregando BED: ${fullBedUrl}`);
      
      this.bedElement.src = fullBedUrl;
      this.bedElement.volume = 0;
      this.bedElement.currentTime = 0;

      await this.bedElement.load();
      await this.bedElement.play();

      // 🔥 Fade in da BED até volume FIXO (cama constante)
      // A BED NÃO participa do breathing - só é um colchão sonoro
      const fixedBedVolume = this.bedVolume;  // 60% fixo
      const fadeSteps = 10;
      const fadeDelay = 50;
      for (let i = 0; i < fadeSteps; i++) {
        this.bedElement.volume = (fixedBedVolume * (i + 1)) / fadeSteps;
        await new Promise(r => setTimeout(r, fadeDelay));
      }

      this.bedActive = true;
      console.log(`[Coordinator] 🎵 BED iniciada (volume FIXO): ${fixedBedVolume * 100}%`);

    } catch (error) {
      console.error('[Coordinator] ❌ Erro ao iniciar BED:', error.message);
    }
  }

  /**
   * FASE 3: Restaura volume da playlist e para BED
   */
  async restoreDucking() {
    // Parar breathing primeiro
    this.stopBreathing();

    // Parar BED com fade out
    await this._stopBed();

    const audio = this.audioManager?.audioElement;
    if (!audio || !this.isDucked) return;

    // Restaurar playlist
    const steps = 20;
    const stepDelay = 50;
    const start = audio.volume;
    const delta = this.originalVolume - start;

    for (let i = 0; i < steps; i += 1) {
      const t = (i + 1) / steps;
      audio.volume = Math.max(0, Math.min(1, start + delta * t));
      await new Promise((r) => setTimeout(r, stepDelay));
    }

    audio.volume = this.originalVolume;
    this.isDucked = false;

    console.log('[Coordinator] 🔊 Playlist restaurada');
  }

  /**
   * Para a BED com fade out
   * @private
   */
  async _stopBed() {
    if (!this.bedElement || !this.bedActive) return;

    try {
      // Fade out
      const fadeSteps = 15;
      const fadeDelay = 50;
      const startVol = this.bedElement.volume;

      for (let i = 0; i < fadeSteps; i++) {
        this.bedElement.volume = startVol * (1 - (i + 1) / fadeSteps);
        await new Promise(r => setTimeout(r, fadeDelay));
      }

      this.bedElement.pause();
      this.bedElement.src = '';
      this.bedElement.volume = 0;
      this.bedActive = false;

      console.log('[Coordinator] 🎵 BED parada');

    } catch (error) {
      console.error('[Coordinator] ❌ Erro ao parar BED:', error.message);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // 🔥 BREATHING NA PLAYLIST (BED fica com volume fixo)
  // ═══════════════════════════════════════════════════════════════

  /**
   * Inicia breathing NA PLAYLIST durante a locução
   * Níveis diretos: 20% durante fala → 90% nas pausas
   * 
   * @param {HTMLAudioElement} locutorElement - Elemento de áudio do locutor
   * @param {Array} pauseMap - Mapa de pausas
   * @param {string} bedUrl - Ignorado (não usamos BED)
   */
  startBreathing(locutorElement, pauseMap, bedUrl = null) {
    if (!pauseMap || pauseMap.length === 0) {
      console.log('[Coordinator] ℹ️ Sem pauseMap - sem breathing');
      return;
    }

    // 🔥 SEMPRE usar a PLAYLIST para breathing
    const targetElement = this.audioManager?.audioElement;
    if (!targetElement) return;

    this.currentPauseMap = pauseMap;
    this.breathingActive = true;
    this.breathingStartTime = Date.now();

    console.log(`[Coordinator] 🎭 Breathing pronto (${pauseMap.length} eventos) - aguardando play...`);

    let lastEventIndex = -1;

    this.breathingInterval = setInterval(() => {
      if (!this.breathingActive || !locutorElement) {
        this.stopBreathing();
        return;
      }

      // 🔥 Só executar se o áudio estiver TOCANDO
      if (locutorElement.paused) {
        return;  // Aguarda o play()
      }

      const currentTime = locutorElement.currentTime;

      // Encontrar evento atual
      let currentEventIndex = -1;
      for (let i = this.currentPauseMap.length - 1; i >= 0; i--) {
        if (currentTime >= this.currentPauseMap[i].time) {
          currentEventIndex = i;
          break;
        }
      }

      // Se mudou de evento, aplicar transição
      if (currentEventIndex !== lastEventIndex && currentEventIndex >= 0) {
        const event = this.currentPauseMap[currentEventIndex];
        lastEventIndex = currentEventIndex;

        // 🔥 Aplicar nível na playlist
        const targetLevel = event.level * this.originalVolume;
        const transition = event.transition || 0.06;

        console.log(
          `[Coordinator] 🎭 PLAYLIST ${event.action} → ${(event.level * 100).toFixed(0)}% (${(transition * 1000).toFixed(0)}ms)`
        );

        this._fastTransition(targetElement, targetLevel, transition);
      }
    }, 30);
  }

  /**
   * Para o breathing
   */
  stopBreathing() {
    if (this.breathingInterval) {
      clearInterval(this.breathingInterval);
      this.breathingInterval = null;
    }
    
    if (this.currentTransition) {
      clearTimeout(this.currentTransition);
      this.currentTransition = null;
    }
    
    this.breathingActive = false;
    this.currentPauseMap = null;
    this.breathingStartTime = null;
  }

  /**
   * Transição rápida de volume
   * @private
   */
  _fastTransition(audioElement, targetLevel, durationSeconds) {
    if (this.currentTransition) {
      clearTimeout(this.currentTransition);
      this.currentTransition = null;
    }

    const startLevel = audioElement.volume;
    const delta = targetLevel - startLevel;
    const durationMs = durationSeconds * 1000;
    
    if (durationMs <= 80) {
      // Ultra-rápida
      const steps = Math.max(2, Math.ceil(durationMs / 20));
      const stepDelay = durationMs / steps;
      let currentStep = 0;

      const doStep = () => {
        if (!this.breathingActive || currentStep >= steps) {
          this.currentTransition = null;
          return;
        }

        currentStep++;
        const progress = currentStep / steps;
        audioElement.volume = Math.max(0, Math.min(1, startLevel + delta * progress));

        if (currentStep < steps) {
          this.currentTransition = setTimeout(doStep, stepDelay);
        } else {
          audioElement.volume = Math.max(0, Math.min(1, targetLevel));
          this.currentTransition = null;
        }
      };

      doStep();
    } else {
      // Normal com easing
      const steps = Math.max(3, Math.floor(durationMs / 25));
      const stepDelay = durationMs / steps;
      let currentStep = 0;

      const doStep = () => {
        if (!this.breathingActive || currentStep >= steps) {
          this.currentTransition = null;
          return;
        }

        currentStep++;
        const progress = currentStep / steps;
        const eased = progress < 0.5 
          ? 2 * progress * progress 
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        audioElement.volume = Math.max(0, Math.min(1, startLevel + delta * eased));

        if (currentStep < steps) {
          this.currentTransition = setTimeout(doStep, stepDelay);
        } else {
          audioElement.volume = Math.max(0, Math.min(1, targetLevel));
          this.currentTransition = null;
        }
      };

      doStep();
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // CONFIGURAÇÃO DA BED
  // ═══════════════════════════════════════════════════════════════

  /**
   * Define volume base da BED
   */
  setBedVolume(volume) {
    this.bedVolume = Math.max(0, Math.min(1, volume));
    if (this.bedElement && this.bedActive) {
      this.bedElement.volume = this.bedVolume;
    }
    console.log(`[Coordinator] 🎚️ BED volume: ${(this.bedVolume * 100).toFixed(0)}%`);
  }

  /**
   * Verifica se BED está disponível
   */
  isBedAvailable() {
    return !!this.bedElement;
  }

  /**
   * Verifica se BED está ativa
   */
  isBedActive() {
    return this.bedActive;
  }

  // ═══════════════════════════════════════════════════════════════
  // COMPAT
  // ═══════════════════════════════════════════════════════════════

  isBlockedRef = {
    get current() {
      return AudioSyncCoordinator.instance?.isBlocked || false;
    },
  };

  willBlockAfterTrack = () => {
    const inst = AudioSyncCoordinator.instance;
    if (!inst) return false;

    const locutorModule = inst.modules.get('locutor');
    if (!locutorModule) return false;

    return !!(locutorModule.isPlaying || locutorModule.isScheduled);
  };
}

export default AudioSyncCoordinator;